import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, CalendarCheck, Code2, Layers, LineChart, MousePointerClick, Percent, Rocket, Store } from "lucide-react";
import { DemoRequestForm } from "@/components/demo/DemoRequestForm";
import { DemoSlotPicker } from "@/components/demo/DemoSlotPicker";
import { loadDemoConfig } from "@/components/demo/DemoCta";
// SalesCloser AI widget disabled on request — re-enable by restoring this import and <SalesCloserInline /> below.
// import { SalesCloserInline } from "@/components/demo/SalesCloserInline";
import api, { API } from "@/lib/api";

const COVERS = [
  [Rocket, "Merchant Onboarding", "See how your company creates and manages its Cyannect presence."],
  [Store, "Storefronts", "Understand how your brand, products, services and promotions are organized."],
  [Layers, "Offers and Promotions", "See how campaigns and offers are created and managed."],
  [MousePointerClick, "Shopper Journey", "See how shoppers discover your brand and continue to your website or app."],
  [LineChart, "Conversion Tracking", "Understand how eligible transactions are attributed and reported."],
  [Percent, "Performance Model", "See how Cyannect pricing and performance fees apply to your selected plan."],
  [BarChart3, "Reporting", "Review campaign and conversion visibility."],
  [Code2, "Integration Options", "See available integration paths for websites, ecommerce systems or SaaS platforms."],
];
const WHO = ["Retail brands", "Ecommerce companies", "SaaS businesses", "Digital-service providers", "Marketing teams",
  "Ecommerce managers", "Growth teams", "Business owners", "Technical/integration teams"];

function Confirmation({ booking, token }) {
  return (
    <div className="text-center py-6" data-testid="demo-confirmation" role="status">
      <CalendarCheck className="w-10 h-10 text-cyan-600 mx-auto" />
      <h2 className="font-display text-2xl text-slate-900 mt-3" data-testid="demo-confirmation-title">
        {booking.moved ? "Your Cyannect Demo Has Been Moved" : "Your Cyannect Demo Is Scheduled"}</h2>
      {booking.moved && <p className="text-sm text-slate-600 mt-1" data-testid="demo-confirmation-moved">Your earlier time has been cancelled.</p>}
      <dl className="mt-5 text-sm text-slate-700 space-y-1">
        <div data-testid="demo-confirmation-date"><dt className="inline font-medium">Date: </dt><dd className="inline">{booking.local_date}</dd></div>
        <div data-testid="demo-confirmation-time"><dt className="inline font-medium">Time: </dt><dd className="inline">{booking.local_time} {booking.tz_abbrev} (Eastern Time, America/Toronto) · 20 minutes</dd></div>
        <div data-testid="demo-confirmation-email"><dt className="inline font-medium">Confirmation sent to: </dt><dd className="inline">{booking.contact_email}</dd></div>
        <div><dt className="inline font-medium">Meeting: </dt><dd className="inline">{booking.meeting_method} — a Cyannect team member will send your meeting link.</dd></div>
      </dl>
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <a href={`${API}/demo/booking.ics?token=${encodeURIComponent(token)}`} className="pf-btn-primary px-5 py-2.5 rounded-full text-sm" data-testid="demo-add-to-calendar">Add to Calendar</a>
        <Link to="/" className="px-5 py-2.5 rounded-full text-sm border border-slate-300" data-testid="demo-return-home">Return to Cyannect</Link>
      </div>
    </div>
  );
}

function ScheduleSteps({ onBooked }) {
  const [slot, setSlot] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const book = async (token, s, replace) => {
    try {
      const r = await api.post("/demo/book", { request_token: token, slot_start_utc: s.start_utc, replace_existing: !!replace });
      onBooked(token, r.data);
    } catch (e) {
      if (e?.response?.data?.detail?.error === "ALREADY_BOOKED") throw e;  // the form asks whether to move the demo
      setSlot(null); setReloadKey((k) => k + 1);
      document.getElementById("demo-step-time")?.scrollIntoView({ behavior: "smooth", block: "start" });
      throw e;
    }
  };
  return (
    <>
      <div id="demo-step-time" className="scroll-mt-24" data-testid="demo-step-time">
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">1. Choose a time</h2>
        <DemoSlotPicker picked={slot} onPick={setSlot} reloadKey={reloadKey} />
      </div>
      <div className="mt-10 pt-8 border-t border-slate-200" data-testid="demo-step-details">
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">2. Tell us about your business</h2>
        <DemoRequestForm slot={slot} onBook={book} />
      </div>
    </>
  );
}

export default function Demo() {
  const [token, setToken] = useState(null);
  const [booking, setBooking] = useState(null);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { loadDemoConfig().then(setCfg); }, []);
  const start = () => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="demo-page">
      <Helmet><title>See How Cyannect Can Help Your Brand Drive Measurable Sales</title>
        <meta name="description" content="Get a personalized walkthrough of how Cyannect helps brands launch promotions, reach shoppers, track conversions and pay based on measurable performance." /></Helmet>
      <section className="max-w-3xl">
        <p className="pf-chip pf-chip-cyan inline-flex">For brands &amp; merchants</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 mt-4" data-testid="demo-hero-title">See How Cyannect Works for Your Brand</h1>
        <p className="text-base md:text-lg text-slate-600 mt-5">Book a personalized Cyannect demo and see how your brand can create a Storefront, launch offers, reach shoppers and track measurable conversions.</p>
        <button type="button" onClick={start} className="pf-btn-primary px-7 py-3 rounded-full mt-8" data-testid="demo-hero-cta">Schedule My Cyannect Demo</button>
      </section>

      <section className="mt-20" aria-labelledby="demo-covers">
        <h2 id="demo-covers" className="text-base md:text-lg font-semibold text-slate-900">What the demo covers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {COVERS.map(([Icon, t, d], i) => (
            <div key={t} className="pf-card p-5 transition-transform hover:-translate-y-0.5" data-testid={`demo-cover-${i}`}>
              <Icon className="w-5 h-5 text-cyan-700" />
              <h3 className="font-medium text-slate-900 mt-3">{t}</h3>
              <p className="text-sm text-slate-600 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* <SalesCloserInline /> */}

      <section className="mt-16 grid lg:grid-cols-[1fr_2fr] gap-10">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Who should book</h2>
          <p className="text-sm text-slate-600 mt-2">Cyannect demos are designed for:</p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700" data-testid="demo-who-list">{WHO.map((w) => <li key={w}>• {w}</li>)}</ul>
        </div>
        <div id="schedule" className="pf-card p-5 sm:p-7 scroll-mt-24 min-w-0" data-testid="demo-schedule-card">
          {booking ? <Confirmation booking={booking} token={token} />
            : !cfg ? null
              : !cfg.enabled ? <p className="text-sm text-slate-600" data-testid="demo-unavailable">Online demo scheduling isn't available right now. Please email sales@cyannect.com.</p>
                : <ScheduleSteps onBooked={(t, b) => { setToken(t); setBooking(b); document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} />}
        </div>
      </section>
    </div>
  );
}
